import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, Loader2, Trash2, FileText, Download, Upload, FileSpreadsheet, Calendar, Users, Lock } from 'lucide-react';
import { toast } from 'sonner';
import TestManagementPanel from '../../components/TestManagementPanel';

const MCQModulePage = ({ embedded = false }) => {
  const [tests, setTests] = useState([]);
  const [classes, setClasses] = useState([]);
  // Sections removed - using classes directly
  const [loading, setLoading] = useState(true);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [testFormData, setTestFormData] = useState({ test_name: '', class_ids: [] });
  const [importClassId, setImportClassId] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const getClassDisplayName = (cls) => (cls.name || '').replace(/^Class\s+/i, '');
  
  // getSectionIdsForClasses - removed, using classes directly
// 

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [testsRes, classesRes] = await Promise.all([
        api.get('/tests?module_type=mcq'),
        api.get('/classes'),
        // // sections API removed - removed
      ]);
      setTests(testsRes.data);
      setClasses(classesRes.data);
      // setSections removed
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkImport = async (classId) => {
    if (!bulkText) {
      toast.error('Please enter questions');
      return;
    }

    try {
      const lines = bulkText.trim().split('\n');
      const questionIds = [];

      for (const line of lines) {
        if (!line.trim()) continue;
        const parts = line.split('|').map(p => p.trim());
        if (parts.length < 6) continue;
        const [question_text, opt1, opt2, opt3, opt4, correctStr] = parts;
        const explanation = parts.length >= 7 ? parts[6] : null;
        const correctOption = parseInt(correctStr) - 1;
        if (correctOption < 0 || correctOption > 3) continue;
        const response = await api.post('/questions', {
          question_text, options: [opt1, opt2, opt3, opt4], correct_option: correctOption,
          explanation: explanation || null,
          class_id: classId, module_type: 'mcq'
        });
        questionIds.push(response.data.id || response.data);
      }
      return questionIds;
    } catch (err) {
      throw err;
    }
  };

  const handleExcelUpload = async (e, classId) => {
    const file = e.target.files?.[0];
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('class_id', classId);

    try {
      const response = await api.post('/questions/bulk-import-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const questionsRes = await api.get(`/questions?module_type=mcq&class_id=${classId}`);
      const questionIds = questionsRes.data.slice(-response.data.created).map(q => q.id);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return questionIds;
    } catch (err) {
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const handleCreateTest = async (e) => {
    e.preventDefault();
    if (!testFormData.test_name || testFormData.class_ids.length === 0 || !bulkText) {
      toast.error('Please fill test name, classes, and questions');
      return;
    }

    // Use first selected class if importClassId not specified
    const questionClassId = importClassId || testFormData.class_ids[0];

    setSaving(true);
    try {
      const questionIds = await handleBulkImport(questionClassId);
      if (!questionIds || questionIds.length === 0) {
        toast.error('No valid questions found. Format: Question|Opt1|Opt2|Opt3|Opt4|CorrectNo(1-4)');
        setSaving(false);
        return;
      }
      // using class_ids directly
      const response = await api.post('/tests', {
        test_name: testFormData.test_name, module_type: 'mcq',
        section_ids: testFormData.class_ids, // Direct class assignment,
        question_ids: questionIds
      });
      toast.success(`${response.data.test_number} created with ${questionIds.length} questions`);
      resetForm(); setTestDialogOpen(false); setBulkText(''); fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create test');
    } finally { setSaving(false); }
  };

  const handleUpdateTest = async (e) => {
    e.preventDefault();
    if (!editingTest) return;
    if (!testFormData.test_name || testFormData.class_ids.length === 0) {
      toast.error('Please fill required fields');
      return;
    }
    setSaving(true);
    try {
      let questionIds = editingTest.questions.map(q => q.id);
      if (bulkText) {
        const questionClassId = importClassId || testFormData.class_ids[0];
        const newIds = await handleBulkImport(questionClassId);
        questionIds = [...questionIds, ...(newIds || [])];
      }
      // using class_ids directly
      await api.put(`/tests/${editingTest.id}`, {
        test_name: testFormData.test_name,
        section_ids: testFormData.class_ids, // Direct class assignment,
        question_ids: questionIds
      });
      toast.success('Test updated');
      resetForm(); setTestDialogOpen(false); setBulkText(''); fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update test');
    } finally { setSaving(false); }
  };

  const resetForm = () => {
    setTestFormData({ test_name: '', class_ids: [] });
    setImportClassId('');
    setEditingTest(null);
  };

  const handleDeleteTest = async (testId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this test?')) return;
    
    try {
      await api.delete(`/tests/${testId}`);
      toast.success('Test deleted successfully');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete test');
    }
  };

  const handleDeleteQuestion = async (testId, questionId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to remove this question from the test?')) return;

    try {
      const test = await api.get(`/tests/${testId}`);
      const updatedQuestionIds = test.data.questions
        .filter(q => q.id !== questionId)
        .map(q => q.id);

      await api.put(`/tests/${testId}`, {
        question_ids: updatedQuestionIds
      });

      toast.success('Question removed from test');
      if (editingTest && editingTest.id === testId) {
        openTestDialog(testId);
      }
      fetchData();
    } catch (err) {
      toast.error('Failed to remove question');
    }
  };

  const openTestDialog = async (testId) => {
    try {
      const response = await api.get(`/tests/${testId}`);
      const testDetails = response.data;
      
      setEditingTest(testDetails);
      // Map sections back to class_ids
      const sectionClassIds = [...new Set(
        (testDetails.classes || []).map(s => {
          return c.id;
        }).filter(Boolean)
      )];
      setTestFormData({
        test_name: testDetails.test_name,
        class_ids: sectionClassIds.length > 0 ? sectionClassIds : (testDetails.classes?.map(s => s.id) || [])
      });
      setImportClassId('');
      setBulkText('');
      setTestDialogOpen(true);
    } catch (err) {
      toast.error('Failed to load test details');
    }
  };

  const handleExportTest = async (testId, e) => {
    e.stopPropagation();
    try {
      const response = await api.get(`/tests/${testId}`);
      const testData = response.data;
      
      let exportText = `Test: ${testData.test_name}\n`;
      exportText += `Module: MCQ\n`;
      exportText += `Created: ${new Date(testData.created_at).toLocaleDateString()}\n`;
      exportText += `\n--- Questions ---\n\n`;
      
      testData.questions.forEach((q, idx) => {
        exportText += `${idx + 1}. ${q.question_text}\n`;
        q.options.forEach((opt, optIdx) => {
          const marker = optIdx === q.correct_option ? '✓' : ' ';
          exportText += `   ${String.fromCharCode(65 + optIdx)}. ${opt} ${marker}\n`;
        });
        exportText += `\n`;
      });

      const blob = new Blob([exportText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${testData.test_name.replace(/\s+/g, '_')}_MCQ.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Test exported successfully');
    } catch (err) {
      toast.error('Failed to export test');
    }
  };

  const toggleClass = (classId) => {
    setTestFormData(prev => ({
      ...prev,
      class_ids: prev.class_ids.includes(classId)
        ? prev.class_ids.filter(id => id !== classId)
        : [...prev.class_ids, classId]
    }));
  };

  const getTestClassesDisplay = (test) => {
    if (!test.classes?.length) return 'No classes';
    return test.classes.map(s => (s.class_name || s.name || '').replace(/^Class\s+/i, '')).join(', ');
  };

  const content = (
    <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              MCQ Test
            </h1>
            <p className="text-slate-500">
              Create and manage MCQ tests with imported questions
            </p>
          </div>
          
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => {
              setEditingTest(null);
              setTestFormData({ test_name: '', class_ids: [] });
              setImportClassId('');
              setBulkText('');
              setTestDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Test
          </Button>
        </div>

        {/* Test Dialog (Create/Edit) */}
        <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTest ? `Edit Test: ${editingTest.test_number}` : 'Create New Test'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={editingTest ? handleUpdateTest : handleCreateTest} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="testName">Test Name / Topic *</Label>
                <Input
                  id="testName"
                  value={testFormData.test_name}
                  onChange={(e) => setTestFormData({ ...testFormData, test_name: e.target.value })}
                  placeholder="e.g., Grammar Assessment - Week 1"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Assign to Classes *</Label>
                  <button type="button" onClick={() => setTestFormData(prev => ({ ...prev, class_ids: prev.class_ids.length === classes.length ? [] : classes.map(c => c.id) }))} className="text-xs text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 font-medium underline-offset-2 hover:underline">
                    {testFormData.class_ids.length === classes.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="border rounded-lg p-4 max-h-52 overflow-y-auto">
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {classes.map(cls => {
                      const dn = getClassDisplayName(cls);
                      const sel = testFormData.class_ids.includes(cls.id);
                      return (
                        <label key={cls.id} className={`flex items-center justify-center p-2 rounded-lg border-2 cursor-pointer text-sm font-semibold transition-all ${sel ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300'}`}>
                          <input type="checkbox" className="sr-only" checked={sel} onChange={() => toggleClass(cls.id)} />
                          {dn}
                        </label>
                      );
                    })}
                  </div>
                </div>
                <p className="text-xs text-slate-500">{testFormData.class_ids.length} class(es) selected</p>
              </div>

              {/* Existing Questions (for edit mode) */}
              {editingTest && editingTest.questions && editingTest.questions.length > 0 && (
                <div className="space-y-2">
                  <Label>Current Questions ({editingTest.questions.length})</Label>
                  <div className="border rounded-lg p-4 max-h-64 overflow-y-auto bg-slate-50 dark:bg-slate-900">
                    {editingTest.questions.map((question, idx) => (
                      <div key={question.id} className="mb-3 p-3 bg-white dark:bg-slate-800 rounded">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-sm font-medium">#{idx + 1}: {question.question_text}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-rose-600"
                            onClick={(e) => handleDeleteQuestion(editingTest.id, question.id, e)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          {question.options?.map((opt, optIdx) => (
                            <div key={optIdx} className={`p-1 rounded ${optIdx === question.correct_option ? 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300' : 'bg-slate-100 dark:bg-slate-700'}`}>
                              {String.fromCharCode(65 + optIdx)}. {opt}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Import Questions Class - HIDDEN (not required, auto-uses first selected class) */}
              <div className="hidden">
                <Label>{editingTest ? 'Add More Questions (Import)' : 'Import Questions'}</Label>
                <Select value={importClassId} onValueChange={setImportClassId}>
                  <SelectTrigger><SelectValue placeholder="Select class for questions" /></SelectTrigger>
                  <SelectContent>
                    {classes.map((cls) => (
                      <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Tabs defaultValue="paste" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="paste">Paste Text</TabsTrigger>
                  <TabsTrigger value="excel">Upload Excel</TabsTrigger>
                </TabsList>
                
                <TabsContent value="paste" className="space-y-2">
                  <Label htmlFor="bulkText">Questions (one per line) {!editingTest && '*'}</Label>
                  <Textarea
                    id="bulkText"
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder="Format: Question|Option1|Option2|Option3|Option4|CorrectOption(1-4)|Explanation(Optional)&#10;What is 2+2?|2|3|4|5|3|Two plus two equals four&#10;Capital of France?|London|Berlin|Paris|Madrid|3"
                    rows={6}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500">
                    {bulkText.split('\n').filter(l => l.trim()).length} question(s) to import
                  </p>
                </TabsContent>
                
                <TabsContent value="excel" className="space-y-2">
                  <div className="p-4 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
                    <div className="text-center">
                      <FileSpreadsheet className="h-10 w-10 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500 mb-2">
                        Upload Excel file (.xlsx)
                      </p>
                      <p className="text-xs text-slate-400 mb-4">
                        Format: QuestionNo | Question | Option1 | Option2 | Option3 | Option4 | CorrectOption
                      </p>
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept=".xlsx,.xls"
                        onChange={(e) => {
                          const questionClassId = importClassId || testFormData.class_ids[0];
                          handleExcelUpload(e, questionClassId);
                        }}
                        className="hidden"
                        id="excel-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                      >
                        {uploading ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        Choose File
                      </Button>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <DialogFooter>
                <Button type="submit" disabled={saving || uploading}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingTest ? 'Update Test' : 'Create Test'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Tests List */}
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          </div>
        ) : tests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No tests created yet</p>
              <p className="text-sm text-slate-400">Click "Create Test" to create your first test</p>
            </CardContent>
          </Card>
        ) : (
          <TestManagementPanel
            tests={tests}
            classes={classes}
            onRefresh={fetchData}
            moduleColor="sky"
            moduleType="mcq"
          />
        )}
      </div>
  );
  if (embedded) return content;
  return <DashboardLayout>{content}</DashboardLayout>;
};

export default MCQModulePage;
